// src/pages/PipelinePage.tsx

import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { useActiveWorkspace } from '../hooks/useActiveWorkspace';
import {
  useGetPipelinesQuery,
  useCreatePipelineMutation,
  useRestorePipelineMutation,
} from '../api/pipelinesApi';
import {
  useGetOpportunitiesQuery,
  useCreateOpportunityMutation,
  useUpdateOpportunityStageMutation,
  useDeleteOpportunityMutation,
} from '../api/opportunitiesApi';
import { useGetContactsQuery } from '../api/contactsApi';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorState from '../components/common/ErrorState';
import Button from '../components/common/Button';
import OpportunityDetailModal from '../components/opportunities/OpportunityDetailModal';
import { DeleteConfirmDialog } from '../components/ui/ConfirmDialog';
import type { Pipeline, PipelineStage, Opportunity, OpportunityStatus } from '../types/api';
import { useHasCapability, useHasRole } from '../services/centralContext';
import type { CentralRole } from '../services/centralContext';
import { FEATURE_CAPABILITIES } from '../config/featureCapabilities';
import Modal from '../components/ui/Modal';
import StageDraftList from '../components/pipelines/StageDraftList';
import PipelineSettingsModal from '../components/pipelines/PipelineSettingsModal';
import {
  defaultStageDrafts,
  isArchived,
  pipelineErrorMessage,
  stageListProblem,
  type StageDraft,
} from '../components/pipelines/stageOutcome';
import type { PipelineArchiveResponse } from '../types/api';
// One failure vocabulary shared with OpportunityDetailModal.
import { ARCHIVED_DEALS_REASON, describeDealFailure } from '../components/opportunities/dealFailure';

const STATUS_COLORS: Record<OpportunityStatus, { bg: string; text: string }> = {
  open: { bg: 'bg-blue-100', text: 'text-blue-700' },
  won: { bg: 'bg-green-100', text: 'text-green-700' },
  lost: { bg: 'bg-red-100', text: 'text-red-700' },
};

/**
 * The workspace roles `requirePermission('pipelines.*')` admits, mirroring
 * AUTHORIZATION_MATRIX['pipelines.create'|'pipelines.update'|'pipelines.delete']
 * on the server. Declared once so the two create controls and the tests all
 * read the same list.
 */
const PIPELINE_ADMIN_ROLES: CentralRole[] = ['owner', 'admin'];

export default function PipelinePage() {
  // Replaced with useActiveWorkspace
  const activeWorkspace = useActiveWorkspace();
  const workspaceId = activeWorkspace?.workspace.id;

  // `/pipeline?pipelineId=<id>` deep-links a pipeline — active OR archived.
  // The selection is written back to the URL so the board can be shared.
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedPipelineId = searchParams.get('pipelineId');
  const [selectedPipelineId, setSelectedPipelineIdState] = useState<string | null>(linkedPipelineId);
  const setSelectedPipelineId = (id: string | null) => {
    setSelectedPipelineIdState(id);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set('pipelineId', id);
        else next.delete('pipelineId');
        return next;
      },
      { replace: true },
    );
  };
  /** Archived pipelines are listed only on request (or when one is selected). */
  const [showArchived, setShowArchived] = useState(false);
  /** Outcome of an archive / restore, shown above the board. */
  const [boardNotice, setBoardNotice] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  // Managing a pipeline takes BOTH gates, because routes/pipelines.ts applies
  // both and does so deliberately: `requirePermission('pipelines.create')`
  // (workspace role owner/admin) AND `requireCapabilityMiddleware('deals',
  // 'admin')`. That file's header explains why — a pipeline defines the sales
  // process for the whole workspace, so it keeps the legacy owner/admin floor
  // rather than becoming delegable by capability alone. AND, never OR.
  //
  // CHECKING ONLY THE CAPABILITY IS NOT ENOUGH, and assuming otherwise is the
  // exact mistake this page already made once. Central's access editor lets an
  // owner tick "Manage pipelines & stages" for a MEMBER, which grants
  // `crm:deals:admin` and nothing else. Such a member passes the capability
  // gate and is then refused by the role floor with INSUFFICIENT_ROLE — so a
  // capability-only check here would show the button to precisely the person
  // whose click cannot succeed, which is the defect, not the fix.
  const hasPipelineAdminCapability = useHasCapability(
    FEATURE_CAPABILITIES.managePipelines.domain,
    FEATURE_CAPABILITIES.managePipelines.action
  );
  // The role floor the server applies first. `useHasRole` is the canonical
  // reader for it; nothing here compares a role string by hand.
  const hasPipelineAdminRole = useHasRole(PIPELINE_ADMIN_ROLES);
  const canManagePipelines = hasPipelineAdminCapability && hasPipelineAdminRole;

  // FU-2 — ORDINARY SALES WORK, and a different bar from the one above.
  //
  // routes/opportunities.ts is capability-ONLY and there is no role floor on
  // it, deliberately: creating a deal, editing it and moving it between
  // stages is `deals:write`, and deleting is `deals:delete`. A salesperson
  // needs no administrator authority for any of it.
  //
  // The page offered all of it to everyone who could merely SEE it. A reader
  // holding only `deals:read` got a "+" button, a stage dropdown, a Delete
  // link and working drag-and-drop — four controls whose every use is a 403.
  const canWriteDeals = useHasCapability(
    FEATURE_CAPABILITIES.editDeal.domain,
    FEATURE_CAPABILITIES.editDeal.action
  );
  const canDeleteDeals = useHasCapability(
    FEATURE_CAPABILITIES.deleteDeal.domain,
    FEATURE_CAPABILITIES.deleteDeal.action
  );

  /**
   * A truthful reason the last opportunity mutation failed.
   *
   * Separate from `pipelineError` because the two appear in different places —
   * that error belongs to the create-pipeline modal, this one to the board —
   * and sharing one slot would show a stale pipeline message above the deals.
   */
  const [dealError, setDealError] = useState<string | null>(null);
  /**
   * Refusals that belong INSIDE a dialog rather than on the board.
   *
   * `DeleteConfirmDialog` already takes an `error` prop and the create modal
   * gained an `errorMessage` in PR #377, so the reason is shown where the
   * user's attention already is, and the dialog stays open with their input
   * intact — the same rule that PR applied one modal over. `dealError` above
   * is for the move, which has no dialog to own it.
   */
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [createDealError, setCreateDealError] = useState<string | null>(null);

  /** A truthful reason the last create failed. Never left silent. */
  const [pipelineError, setPipelineError] = useState<string | null>(null);
  const [showCreatePipelineModal, setShowCreatePipelineModal] = useState(false);
  const [showCreateOpportunityModal, setShowCreateOpportunityModal] = useState(false);
  const [createInStageId, setCreateInStageId] = useState<string | null>(null);
  const [selectedOpportunityId, setSelectedOpportunityId] = useState<string | null>(null);
  const [opportunityToDelete, setOpportunityToDelete] = useState<Opportunity | null>(null);

  const {
    data: pipelines,
    isLoading: pipelinesLoading,
    error: pipelinesError,
    refetch: refetchPipelines,
  } = useGetPipelinesQuery({ workspaceId, status: 'all' }, { skip: !workspaceId });

  // The server lists active pipelines first; archived ones are kept apart so
  // the selector offers them only on request.
  const activePipelines = useMemo(() => (pipelines ?? []).filter((p) => !isArchived(p)), [pipelines]);
  const archivedPipelines = useMemo(() => (pipelines ?? []).filter((p) => isArchived(p)), [pipelines]);

  const selectedPipeline = useMemo(() => {
    if (!pipelines?.length) return null;
    if (selectedPipelineId) {
      const chosen = pipelines.find((p) => p.id === selectedPipelineId);
      if (chosen) return chosen;
    }
    // Never fall back to an ARCHIVED pipeline: the board opens on a live one.
    return activePipelines.find((p) => p.isDefault) || activePipelines[0] || null;
  }, [pipelines, activePipelines, selectedPipelineId]);

  const selectedIsArchived = isArchived(selectedPipeline);
  /** A deep link that names no pipeline this workspace can see. Said, not silently swapped. */
  const linkUnavailable =
    !!linkedPipelineId && !!pipelines && selectedPipelineId === linkedPipelineId &&
    !pipelines.some((p) => p.id === linkedPipelineId);
  const listArchived = showArchived || selectedIsArchived;
  /**
   * Adding, dragging and moving deals on THIS board. `deals:write` alone is
   * not enough on an archived pipeline — the server refuses with
   * PIPELINE_ARCHIVED, so the controls are not offered.
   */
  const boardCanWriteDeals = canWriteDeals && !selectedIsArchived;

  const {
    data: opportunities,
    isLoading: opportunitiesLoading,
  } = useGetOpportunitiesQuery(
    { workspaceId, pipelineId: selectedPipeline?.id },
    { skip: !workspaceId || !selectedPipeline?.id }
  );

  const { data: contactsResponse } = useGetContactsQuery({ workspaceId }, { skip: !workspaceId });
  const contacts = contactsResponse?.contacts;

  const [createPipeline, { isLoading: isCreatingPipeline }] = useCreatePipelineMutation();
  const [createOpportunity, { isLoading: isCreatingOpportunity }] = useCreateOpportunityMutation();
  const [updateOpportunityStage] = useUpdateOpportunityStageMutation();
  const [deleteOpportunity] = useDeleteOpportunityMutation();
  const [restorePipeline, { isLoading: isRestoringPipeline }] = useRestorePipelineMutation();

  const handleRestoreSelected = async () => {
    if (!selectedPipeline) return;
    setBoardNotice(null);
    try {
      await restorePipeline(selectedPipeline.id).unwrap();
      setBoardNotice({ kind: 'ok', text: `Restored “${selectedPipeline.name}”. Deals can be added and moved again.` });
    } catch (error) {
      setBoardNotice({ kind: 'error', text: pipelineErrorMessage(error, 'Could not restore the pipeline.') });
    }
  };

  const handleArchiveChange = (kind: 'archived' | 'restored', result: PipelineArchiveResponse) => {
    const name = result.pipeline?.name ?? 'the pipeline';
    if (kind === 'archived') {
      setShowPipelineSettings(false);
      // Stay on it: the user sees exactly what archiving did — the same deals,
      // no longer addable or movable between stages.
      setShowArchived(true);
      setSelectedPipelineId(result.pipeline?.id ?? selectedPipeline?.id ?? null);
      const autos = result.automationsReferencing ?? [];
      setBoardNotice({
        kind: 'ok',
        text:
          `Archived “${name}”. Its deals are kept; new deals and stage moves are off until it is restored.` +
          (autos.length
            ? ` Automations that still use it: ${autos.join(', ')} — their deal steps will fail until you change them or restore the pipeline.`
            : ''),
      });
    } else {
      setBoardNotice({ kind: 'ok', text: `Restored “${name}”. Deals can be added and moved again.` });
    }
  };

  const [pipelineFormData, setPipelineFormData] = useState({
    name: '',
    description: '',
  });
  // The New Pipeline stage list starts from the default template and is fully
  // editable before Create; it is sent whole and created in one transaction.
  const [stageDrafts, setStageDrafts] = useState<StageDraft[]>(defaultStageDrafts);
  const [showPipelineSettings, setShowPipelineSettings] = useState(false);

  const [opportunityFormData, setOpportunityFormData] = useState({
    title: '',
    amount: '',
    expectedCloseDate: '',
    contactId: '',
    description: '',
  });

  // Group opportunities by stageId
  const opportunitiesByStage = useMemo(() => {
    const grouped: Record<string, Opportunity[]> = {};
    selectedPipeline?.stages?.forEach((stage) => {
      grouped[stage.id] = [];
    });
    opportunities?.forEach((opp) => {
      if (grouped[opp.stageId]) {
        grouped[opp.stageId].push(opp);
      }
    });
    return grouped;
  }, [opportunities, selectedPipeline?.stages]);

  // Calculate totals per stage
  const stageTotals = useMemo(() => {
    const totals: Record<string, { count: number; value: number }> = {};
    selectedPipeline?.stages?.forEach((stage) => {
      const stageOpps = opportunitiesByStage[stage.id] || [];
      totals[stage.id] = {
        count: stageOpps.length,
        value: stageOpps.reduce((sum, opp) => sum + (opp.amount || 0), 0),
      };
    });
    return totals;
  }, [opportunitiesByStage, selectedPipeline?.stages]);

  const resetPipelineForm = () => {
    setPipelineFormData({ name: '', description: '' });
    setStageDrafts(defaultStageDrafts());
  };

  const resetOpportunityForm = () => {
    setOpportunityFormData({
      title: '',
      amount: '',
      expectedCloseDate: '',
      contactId: '',
      description: '',
    });
  };

  const handleCreatePipeline = async (e: React.FormEvent) => {
    e.preventDefault();
    setPipelineError(null);

    // Previously `if (!workspaceId) return;` — a bare return, so pressing
    // Create did nothing at all and said nothing at all.
    if (!workspaceId) {
      setPipelineError('No active workspace. Reload the page and try again.');
      return;
    }

    const problem = stageListProblem(stageDrafts);
    if (problem) {
      setPipelineError(problem);
      return;
    }

    try {
      const result = await createPipeline({
        workspaceId,
        name: pipelineFormData.name,
        description: pipelineFormData.description || undefined,
        stages: stageDrafts.map((s) => ({
          name: s.name.trim(),
          outcome: s.outcome,
          probability: s.probability,
        })),
      }).unwrap();
      setSelectedPipelineId(result.id);
      setShowCreatePipelineModal(false);
      resetPipelineForm();
    } catch (error) {
      // The defect this replaces: the error went to console.error and NOWHERE
      // ELSE. The member pressed Create, the server answered 403, and the
      // button simply appeared broken.
      //
      // THE TWO 403s ARE DIFFERENT ANSWERS AND MUST NOT SHARE ONE MESSAGE.
      // routes/pipelines.ts runs the role gate FIRST, so which body comes back
      // says which requirement was missing:
      //
      //   { error: 'INSUFFICIENT_ROLE', requiredRoles: [...] }
      //       — permissionService.requirePermission. The person may well hold
      //         "Manage pipelines & stages"; the workspace ROLE is the blocker,
      //         and telling them to ask for a capability they already have
      //         sends them to the wrong place.
      //
      //   { code: 'CAPABILITY_DENIED', message: 'Missing capability: ...' }
      //       — requireCapabilityMiddleware. Here the grant genuinely is
      //         missing and an owner can fix it in Central.
      const status = (error as { status?: number } | undefined)?.status;
      const data = (error as { data?: { code?: string; error?: string } } | undefined)?.data;
      const code = data?.code;
      const serverError = data?.error;
      setPipelineError(
        status === 401
          ? 'Your session has expired. Reload the page and sign in again.'
          : serverError === 'INSUFFICIENT_ROLE'
            ? 'Creating and changing pipelines is limited to workspace owners and administrators. Your access to manage pipelines cannot take effect at your current workspace role — ask an owner to review it.'
            : code === 'CAPABILITY_DENIED'
              ? 'You have not been granted “Manage pipelines & stages” for this workspace. An owner or administrator can grant it under Settings → Members, Roles & Access.'
              : status === 403
                ? 'You do not have permission to create pipelines in this workspace.'
                : status === 400
                  ? 'That pipeline could not be created — check the name and try again.'
                  : 'Could not create the pipeline. Please try again.'
      );
    }
  };

  const closeCreateDeal = () => {
    setShowCreateOpportunityModal(false);
    setCreateInStageId(null);
    resetOpportunityForm();
  };

  const handleCreateOpportunity = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateDealError(null);
    // Previously a bare `return` for all three — the button did nothing and
    // said nothing, exactly the defect PR #377 fixed one modal over.
    if (!workspaceId) {
      setCreateDealError('No active workspace. Reload the page and try again.');
      return;
    }
    if (!selectedPipeline || !createInStageId) {
      setCreateDealError('No pipeline stage selected. Close this and press + on a stage.');
      return;
    }

    try {
      await createOpportunity({
        workspaceId,
        pipelineId: selectedPipeline.id,
        stageId: createInStageId,
        title: opportunityFormData.title,
        amount: opportunityFormData.amount ? parseFloat(opportunityFormData.amount) : undefined,
        expectedCloseDate: opportunityFormData.expectedCloseDate || undefined,
        contactId: opportunityFormData.contactId || undefined,
        description: opportunityFormData.description || undefined,
      }).unwrap();
      setShowCreateOpportunityModal(false);
      setCreateInStageId(null);
      resetOpportunityForm();
    } catch (error) {
      setCreateDealError(describeDealFailure(error, 'create'));
    }
  };

  const handleMoveOpportunity = async (opportunityId: string, newStageId: string) => {
    setDealError(null);
    try {
      await updateOpportunityStage({ id: opportunityId, stageId: newStageId }).unwrap();
    } catch (error) {
      // The board self-corrects: the mutation invalidates the opportunity
      // tags, so a refused move re-renders from the server and the card
      // returns to its real stage. What was missing was any statement that it
      // had been refused, which made the card appear to spring back for no
      // reason.
      setDealError(describeDealFailure(error, 'move'));
    }
  };

  const handleDeleteClick = (opportunity: Opportunity) => {
    // Clear any refusal from a previous attempt, so a stale reason cannot
    // greet the next deal the user opens.
    setDeleteError(null);
    setOpportunityToDelete(opportunity);
  };

  const handleConfirmDelete = async () => {
    if (!opportunityToDelete) return;
    setDealError(null);
    try {
      await deleteOpportunity(opportunityToDelete.id).unwrap();
      setOpportunityToDelete(null);
    } catch (error) {
      // The dialog STAYS OPEN and reports inside itself. Closing it would
      // discard the reason along with the context, and `DeleteConfirmDialog`
      // already has an `error` slot built for exactly this.
      setDeleteError(describeDealFailure(error, 'delete'));
    }
  };

  const openCreateOpportunityModal = (stageId: string) => {
    setCreateDealError(null);
    setCreateInStageId(stageId);
    setShowCreateOpportunityModal(true);
  };

  const handleDragEnd = async (result: DropResult) => {
    const { destination, source, draggableId } = result;

    // Belt as well as braces. Dragging is disabled below for a reader, but a
    // capability can be revoked between render and drop, and the library can
    // be driven from the keyboard. An archived pipeline's deals do not move.
    if (!boardCanWriteDeals) return;

    // Dropped outside a droppable area
    if (!destination) return;

    // Dropped in same position
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    // Get the new stage ID from the droppable ID
    const newStageId = destination.droppableId;

    // Only update if stage changed
    if (newStageId !== source.droppableId) {
      setDealError(null);
      try {
        await updateOpportunityStage({ id: draggableId, stageId: newStageId }).unwrap();
      } catch (error) {
        setDealError(describeDealFailure(error, 'move'));
      }
    }
  };

  if (pipelinesLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (pipelinesError) {
    return <ErrorState message="Failed to load pipelines." onRetry={refetchPipelines} />;
  }

  if (!activeWorkspace) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Please select a workspace to view your pipeline.</p>
      </div>
    );
  }

  // No pipelines at all (active or archived) - show create prompt
  if (!pipelines?.length) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pipeline</h1>
          <p className="text-sm text-gray-500">Manage your deals</p>
        </div>
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">No Pipeline Yet</h3>
          <p className="text-gray-500 mb-4">
            {canManagePipelines
              ? 'Create your first pipeline to start tracking deals.'
              : 'No pipeline has been set up yet. Ask a workspace administrator to create one.'}
          </p>
          {canManagePipelines && (
            <Button onClick={() => setShowCreatePipelineModal(true)}>Create Pipeline</Button>
          )}
        </div>

        {/* Create Pipeline Modal */}
        {showCreatePipelineModal && (
          <CreatePipelineModal
            formData={pipelineFormData}
            setFormData={setPipelineFormData}
            stages={stageDrafts}
            setStages={setStageDrafts}
            onSubmit={handleCreatePipeline}
            onClose={() => {
              setShowCreatePipelineModal(false);
              setPipelineError(null);
              resetPipelineForm();
            }}
            isLoading={isCreatingPipeline}
            errorMessage={pipelineError}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pipeline</h1>
          <p className="text-sm text-gray-500">
            {selectedPipeline
              ? `${opportunities?.length || 0} deal${opportunities?.length !== 1 ? 's' : ''} in pipeline`
              : 'No active pipeline'}
          </p>
        </div>
        {/* Wraps on narrow screens: the selector takes the row next to the
            settings button and "New Pipeline" moves to the next line, instead
            of the row running past the viewport (the layout clips horizontal
            overflow, so nothing would scroll — the controls would just be cut). */}
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:gap-3">
          {/* Pipeline Selector — the shared select styling (index.css) gives
              the caret its own inset and padding; width is bounded so a long
              name truncates instead of pushing the actions off the row. */}
          {activePipelines.length + (listArchived ? archivedPipelines.length : 0) > 1 && (
            <>
              <label htmlFor="pipeline-selector" className="sr-only">
                Pipeline
              </label>
              <select
                id="pipeline-selector"
                value={selectedPipeline?.id || ''}
                onChange={(e) => setSelectedPipelineId(e.target.value)}
                className="h-10 min-w-0 flex-1 truncate rounded-lg border border-gray-300 bg-white pl-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 sm:min-w-[10rem] sm:max-w-[16rem] sm:flex-none"
              >
                {!selectedPipeline && <option value="">Choose a pipeline…</option>}
                {activePipelines.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
                {listArchived && archivedPipelines.length > 0 && (
                  <optgroup label="Archived">
                    {archivedPipelines.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} (archived)
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            </>
          )}
          {/* Archived pipelines are hidden until asked for. The toggle says how
              many there are, so "hidden" never reads as "gone". */}
          {archivedPipelines.length > 0 && (
            <button
              type="button"
              aria-pressed={listArchived}
              onClick={() => {
                if (listArchived) {
                  setShowArchived(false);
                  // Hiding archived pipelines while viewing one returns to a live one.
                  if (selectedIsArchived) setSelectedPipelineId(null);
                } else {
                  setShowArchived(true);
                }
              }}
              className="h-10 shrink-0 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {listArchived ? 'Hide archived' : `Show archived (${archivedPipelines.length})`}
            </button>
          )}
          {canManagePipelines && selectedPipeline && (
            <button
              type="button"
              onClick={() => setShowPipelineSettings(true)}
              aria-label={`Settings for ${selectedPipeline.name}`}
              title="Pipeline settings"
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
          )}
          {canManagePipelines && (
            <Button variant="secondary" onClick={() => setShowCreatePipelineModal(true)}>
              New Pipeline
            </Button>
          )}
        </div>
      </div>

      {linkUnavailable && (
        <div role="alert" data-testid="pipeline-link-unavailable" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          The linked pipeline is not available in this workspace — it may have been deleted.
          {selectedPipeline ? ` Showing “${selectedPipeline.name}” instead.` : ''}
        </div>
      )}

      {boardNotice && (
        <div
          role={boardNotice.kind === 'error' ? 'alert' : 'status'}
          data-testid="pipeline-notice"
          className={`flex items-start justify-between gap-3 rounded-lg border px-3 py-2 text-sm ${
            boardNotice.kind === 'error' ? 'border-red-200 bg-red-50 text-red-800' : 'border-green-200 bg-green-50 text-green-800'
          }`}
        >
          <span className="min-w-0 break-words">{boardNotice.text}</span>
          <button type="button" onClick={() => setBoardNotice(null)} aria-label="Dismiss" className="shrink-0">
            &times;
          </button>
        </div>
      )}

      {/* An ARCHIVED pipeline is shown truthfully: same deals, no adding or
          stage moves, labelled, with the way back for those who may take it. */}
      {selectedPipeline && selectedIsArchived && (
        <div
          data-testid="archived-banner"
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-800"
        >
          <p className="min-w-0 break-words">
            <span className="mr-2 inline-block rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700">
              Archived
            </span>
            <strong className="font-medium">{selectedPipeline.name}</strong> is archived. Deals are kept; new deals
            can't be added and deals can't move between stages until it is restored.
            {!canManagePipelines && ' A workspace owner or administrator can restore it.'}
          </p>
          {canManagePipelines && (
            <Button variant="secondary" onClick={() => void handleRestoreSelected()} isLoading={isRestoringPipeline}>
              Restore pipeline
            </Button>
          )}
        </div>
      )}

      {!selectedPipeline && (
        <div data-testid="no-active-pipeline" className="rounded-xl border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-600">
          Every pipeline in this workspace is archived.{' '}
          {canManagePipelines ? 'Create a new pipeline, or show archived pipelines to view or restore one.' : 'Show archived pipelines to view one.'}
        </div>
      )}

      {/* A refused deal mutation says so HERE, above the board, because the
          board is where the user was when it failed. Previously all three —
          create, move, delete — went to console.error and nowhere else. */}
      {dealError && (
        <div
          role="alert"
          data-testid="deal-error"
          className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 flex items-start justify-between gap-3"
        >
          <span>{dealError}</span>
          <button
            type="button"
            onClick={() => setDealError(null)}
            aria-label="Dismiss"
            className="shrink-0 text-red-700 hover:text-red-900"
          >
            &times;
          </button>
        </div>
      )}

      {/* Board View with Drag and Drop */}
      {opportunitiesLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-4 min-w-max">
              {selectedPipeline?.stages
                ?.slice()
                .sort((a, b) => a.order - b.order)
                .map((stage) => (
                  <StageColumn
                    key={stage.id}
                    stage={stage}
                    opportunities={opportunitiesByStage[stage.id] || []}
                    totals={stageTotals[stage.id]}
                    allStages={selectedPipeline.stages || []}
                    canWriteDeals={boardCanWriteDeals}
                    canDeleteDeals={canDeleteDeals}
                    onAddOpportunity={() => openCreateOpportunityModal(stage.id)}
                    onMoveOpportunity={handleMoveOpportunity}
                    onDeleteOpportunity={handleDeleteClick}
                    onOpportunityClick={setSelectedOpportunityId}
                  />
                ))}
            </div>
          </div>
        </DragDropContext>
      )}

      {/* Create Pipeline Modal */}
      {showCreatePipelineModal && (
        <CreatePipelineModal
          formData={pipelineFormData}
          setFormData={setPipelineFormData}
          stages={stageDrafts}
          setStages={setStageDrafts}
          onSubmit={handleCreatePipeline}
          onClose={() => {
            setShowCreatePipelineModal(false);
            setPipelineError(null);
            resetPipelineForm();
          }}
          isLoading={isCreatingPipeline}
          errorMessage={pipelineError}
        />
      )}

      {/* Pipeline settings — rename, stages, delete. */}
      {showPipelineSettings && selectedPipeline && canManagePipelines && (
        <PipelineSettingsModal
          pipelineId={selectedPipeline.id}
          onClose={() => setShowPipelineSettings(false)}
          onDeleted={() => {
            setShowPipelineSettings(false);
            setSelectedPipelineId(null);
          }}
          onArchiveChange={handleArchiveChange}
        />
      )}

      {/* Create Deal Modal — the shared Modal supplies the dialog role and
          name, focus trap, Escape and focus return. It has no height cap, so
          the body scrolls here. Overlay clicks don't discard typed input. */}
      <Modal
        isOpen={showCreateOpportunityModal}
        onClose={closeCreateDeal}
        title="New Deal"
        size="md"
        closeOnOverlayClick={false}
      >
          <div className="max-h-[70vh] overflow-y-auto -m-4 p-4">
            {/* Same slot the create-pipeline modal gained in PR #377: the
                reason appears where the attempt was made, and the typed form
                survives so the user can correct rather than retype. */}
            {createDealError && (
              <div
                role="alert"
                data-testid="create-deal-error"
                className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
              >
                {createDealError}
              </div>
            )}
            <form onSubmit={handleCreateOpportunity} className="space-y-4">
              <div>
                <label htmlFor="new-deal-title" className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  id="new-deal-title"
                  type="text"
                  value={opportunityFormData.title}
                  onChange={(e) =>
                    setOpportunityFormData({ ...opportunityFormData, title: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Deal Value
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                    $
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    value={opportunityFormData.amount}
                    onChange={(e) =>
                      setOpportunityFormData({ ...opportunityFormData, amount: e.target.value })
                    }
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expected Close Date
                </label>
                <input
                  type="date"
                  value={opportunityFormData.expectedCloseDate}
                  onChange={(e) =>
                    setOpportunityFormData({
                      ...opportunityFormData,
                      expectedCloseDate: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact
                </label>
                <select
                  value={opportunityFormData.contactId}
                  onChange={(e) =>
                    setOpportunityFormData({ ...opportunityFormData, contactId: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">None</option>
                  {contacts?.map((contact) => (
                    <option key={contact.id} value={contact.id}>
                      {contact.fullName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={opportunityFormData.description}
                  onChange={(e) =>
                    setOpportunityFormData({ ...opportunityFormData, description: e.target.value })
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={closeCreateDeal}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" isLoading={isCreatingOpportunity} className="flex-1">
                  Create
                </Button>
              </div>
            </form>
          </div>
      </Modal>

      {/* Opportunity Detail Modal */}
      {workspaceId && selectedPipeline && (
        <OpportunityDetailModal
          opportunityId={selectedOpportunityId}
          workspaceId={workspaceId}
          stages={selectedPipeline.stages || []}
          onClose={() => setSelectedOpportunityId(null)}
          stageReadOnlyReason={selectedIsArchived ? ARCHIVED_DEALS_REASON : null}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        isOpen={!!opportunityToDelete}
        itemName={opportunityToDelete?.title}
        itemType="deal"
        error={deleteError}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setOpportunityToDelete(null);
          setDeleteError(null);
        }}
      />
    </div>
  );
}

// Stage Column Component
interface StageColumnProps {
  stage: PipelineStage;
  opportunities: Opportunity[];
  totals: { count: number; value: number };
  allStages: PipelineStage[];
  /** `deals:write` — may add, move and edit deals here. */
  canWriteDeals: boolean;
  /** `deals:delete` — strictly narrower; owner/admin baseline at Central. */
  canDeleteDeals: boolean;
  onAddOpportunity: () => void;
  onMoveOpportunity: (opportunityId: string, newStageId: string) => void;
  onDeleteOpportunity: (opportunity: Opportunity) => void;
  onOpportunityClick: (id: string) => void;
}

function StageColumn({
  stage,
  opportunities,
  totals,
  allStages,
  canWriteDeals,
  canDeleteDeals,
  onAddOpportunity,
  onMoveOpportunity,
  onDeleteOpportunity,
  onOpportunityClick,
}: StageColumnProps) {
  const stageColor = stage.isClosedWon
    ? 'border-green-300 bg-green-50'
    : stage.isClosedLost
      ? 'border-red-300 bg-red-50'
      : 'border-gray-200 bg-gray-50';

  const headerColor = stage.isClosedWon
    ? 'text-green-700'
    : stage.isClosedLost
      ? 'text-red-700'
      : 'text-gray-700';

  return (
    <div className={`w-72 flex-shrink-0 rounded-xl border ${stageColor} p-3`}>
      {/* Stage Header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className={`font-semibold ${headerColor}`}>{stage.name}</h3>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span>{totals.count} deal{totals.count !== 1 ? 's' : ''}</span>
            <span>|</span>
            <span>${totals.value.toLocaleString()}</span>
          </div>
        </div>
        {/* Adding a deal is `deals:write`. A reader was shown this and every
            press was a 403 the board never mentioned. */}
        {canWriteDeals && (
          <button
            onClick={onAddOpportunity}
            aria-label={`Add a deal to ${stage.name}`}
            data-testid={`add-deal-${stage.id}`}
            className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-white/50 text-gray-500 hover:text-gray-700"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        )}
      </div>

      {/* Opportunity Cards with Droppable */}
      <Droppable droppableId={stage.id}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`space-y-2 max-h-[60vh] overflow-y-auto min-h-[100px] rounded-lg transition-colors ${
              snapshot.isDraggingOver ? 'bg-blue-50' : ''
            }`}
          >
            {opportunities.map((opportunity, index) => (
              <Draggable
                key={opportunity.id}
                draggableId={opportunity.id}
                index={index}
                // Dragging IS `PATCH /opportunities/:id/stage`. Leaving it
                // live for a reader made the whole board look interactive
                // while every drop was refused — and the card sprang back
                // with no explanation, which reads as a broken page.
                isDragDisabled={!canWriteDeals}
              >
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                  >
                    <OpportunityCard
                      opportunity={opportunity}
                      allStages={allStages}
                      canWriteDeals={canWriteDeals}
                      canDeleteDeals={canDeleteDeals}
                      onMove={onMoveOpportunity}
                      onDelete={onDeleteOpportunity}
                      onClick={() => onOpportunityClick(opportunity.id)}
                      isDragging={snapshot.isDragging}
                    />
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
}

// Opportunity Card Component
interface OpportunityCardProps {
  opportunity: Opportunity;
  allStages: PipelineStage[];
  canWriteDeals: boolean;
  canDeleteDeals: boolean;
  onMove: (opportunityId: string, newStageId: string) => void;
  onDelete: (opportunity: Opportunity) => void;
  onClick?: () => void;
  isDragging?: boolean;
}

function OpportunityCard({
  opportunity,
  allStages,
  canWriteDeals,
  canDeleteDeals,
  onMove,
  onDelete,
  onClick,
  isDragging,
}: OpportunityCardProps) {
  const statusStyle = STATUS_COLORS[opportunity.status];

  return (
    <div
      className={`bg-white rounded-lg p-3 shadow-sm border border-gray-200 cursor-pointer transition-shadow ${
        isDragging ? 'shadow-lg ring-2 ring-blue-500' : 'hover:shadow-md'
      }`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-2">
        <p className="font-medium text-gray-900 text-sm">{opportunity.title}</p>
        <span className={`text-xs px-2 py-0.5 rounded-full ${statusStyle.bg} ${statusStyle.text}`}>
          {opportunity.status}
        </span>
      </div>

      {opportunity.amount && (
        <p className="text-lg font-semibold text-gray-900 mb-2">
          ${opportunity.amount.toLocaleString()}
        </p>
      )}

      {opportunity.contact && (
        <p className="text-xs text-gray-500 mb-2 truncate">
          {opportunity.contact.fullName}
        </p>
      )}

      {opportunity.expectedCloseDate && (
        <p className="text-xs text-gray-500 mb-2">
          Close: {new Date(opportunity.expectedCloseDate).toLocaleDateString()}
        </p>
      )}

      {/* The whole footer is mutation controls. A reader gets neither, and
          gets no empty bordered strip either — an orphaned separator reads as
          a rendering fault. The card stays a complete, readable record. */}
      {(canWriteDeals || canDeleteDeals) && (
      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        {canWriteDeals && (
        <select
          value={opportunity.stageId}
          onChange={(e) => {
            e.stopPropagation();
            onMove(opportunity.id, e.target.value);
          }}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Move ${opportunity.title} to another stage`}
          data-testid={`move-deal-${opportunity.id}`}
          className="text-xs border border-gray-200 rounded px-1 py-0.5 max-w-[120px]"
        >
          {allStages
            .slice()
            .sort((a, b) => a.order - b.order)
            .map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
        </select>
        )}
        {/* `deals:delete` is STRICTLY NARROWER than write — owner/admin at
            Central, where write includes member. Reusing the write flag here
            would show Delete to every salesperson. */}
        {canDeleteDeals && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(opportunity);
          }}
          data-testid={`delete-deal-${opportunity.id}`}
          className="text-xs text-red-600 hover:text-red-700"
        >
          Delete
        </button>
        )}
      </div>
      )}
    </div>
  );
}

// Create Pipeline Modal Component
interface CreatePipelineModalProps {
  /** A truthful reason the last attempt failed. Rendered, never swallowed. */
  errorMessage?: string | null;
  formData: { name: string; description: string };
  setFormData: (data: { name: string; description: string }) => void;
  stages: StageDraft[];
  setStages: (stages: StageDraft[]) => void;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
  isLoading: boolean;
}

function CreatePipelineModal({
  formData,
  setFormData,
  stages,
  setStages,
  onSubmit,
  onClose,
  isLoading,
  errorMessage,
}: CreatePipelineModalProps) {
  return (
    <Modal isOpen onClose={onClose} title="New Pipeline" size="lg">
      <form onSubmit={onSubmit} className="space-y-4">
        {/* Scrolls inside the dialog so a long stage list never pushes
            Cancel / Create off a small screen. */}
        <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
          <div>
            <label htmlFor="new-pipeline-name" className="block text-sm font-medium text-gray-700 mb-1">
              Pipeline Name *
            </label>
            <input
              id="new-pipeline-name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Sales Pipeline"
              required
            />
          </div>

          <div>
            <label htmlFor="new-pipeline-description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="new-pipeline-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Optional description..."
            />
          </div>

          <StageDraftList stages={stages} onChange={setStages} disabled={isLoading} />
        </div>

        {errorMessage && (
          <p
            className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3"
            role="alert"
          >
            {errorMessage}
          </p>
        )}

        <div className="flex gap-3">
          <Button type="button" variant="secondary" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button type="submit" isLoading={isLoading} className="flex-1">
            Create
          </Button>
        </div>
      </form>
    </Modal>
  );
}
